import clsx from 'clsx';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { useId } from 'react';

export interface FieldShellProps {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  htmlFor?: string;
  children: ReactNode;
}

export function FieldShell({ label, required, hint, error, htmlFor, children }: FieldShellProps) {
  return (
    <div className="tb-field">
      <label className="tb-field__label" htmlFor={htmlFor}>
        {label}
        {required ? (
          <span className="tb-field__required" aria-hidden={true}>
            *
          </span>
        ) : null}
      </label>
      {children}
      {error ? (
        <span className="tb-field__error" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className="tb-hint">{hint}</span>
      ) : null}
    </div>
  );
}

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  hint?: string;
  error?: string;
}

export function TextField({ label, hint, error, className, required, ...rest }: TextFieldProps) {
  const id = useId();
  return (
    <FieldShell label={label} required={required} hint={hint} error={error} htmlFor={id}>
      <input
        {...rest}
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        className={clsx('tb-input', error && 'tb-input--invalid', className)}
      />
    </FieldShell>
  );
}

export interface TextAreaFieldProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'id'
> {
  label: string;
  hint?: string;
  error?: string;
}

export function TextAreaField({
  label,
  hint,
  error,
  className,
  required,
  ...rest
}: TextAreaFieldProps) {
  const id = useId();
  return (
    <FieldShell label={label} required={required} hint={hint} error={error} htmlFor={id}>
      <textarea
        {...rest}
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        className={clsx('tb-textarea', error && 'tb-textarea--invalid', className)}
      />
    </FieldShell>
  );
}

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string;
  hint?: string;
  error?: string;
  options: { value: string; label: string }[];
  placeholder?: string;
}

export function SelectField({
  label,
  hint,
  error,
  options,
  placeholder,
  className,
  required,
  ...rest
}: SelectFieldProps) {
  const id = useId();
  return (
    <FieldShell label={label} required={required} hint={hint} error={error} htmlFor={id}>
      <select {...rest} id={id} required={required} className={clsx('tb-select', className)}>
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

export interface ColorFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  error?: string;
}

export function ColorField({ label, value, onChange, hint, error }: ColorFieldProps) {
  const id = useId();
  return (
    <FieldShell label={label} hint={hint} error={error} htmlFor={id}>
      <div className="tb-color">
        <input
          id={id}
          type="color"
          value={value || '#000000'}
          onChange={(event) => onChange(event.target.value)}
        />
        <input
          className="tb-input"
          value={value}
          spellCheck={false}
          onChange={(event) => onChange(event.target.value)}
          placeholder="#000000"
        />
      </div>
    </FieldShell>
  );
}

export interface SwitchFieldProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: string;
  disabled?: boolean;
}

export function SwitchField({ label, checked, onChange, hint, disabled }: SwitchFieldProps) {
  return (
    <label className="tb-switch">
      <span className="tb-stack" style={{ gap: 2 }}>
        <span className="tb-field__label">{label}</span>
        {hint ? <span className="tb-hint">{hint}</span> : null}
      </span>
      <span className="tb-switch__track" data-on={checked}>
        <span className="tb-switch__thumb" />
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
      </span>
    </label>
  );
}
